import React, { useMemo, useState, useRef } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import Result from "folktale/result";

import {
  Typography,
  Grow,
  Slider,
  TextField,
  ButtonGroup,
  Button,
  Popper,
  Paper,
  Switch,
  ClickAwayListener,
  MenuItem,
  MenuList,
  InputAdornment,
  FormControlLabel
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import { actions } from "reducer/reducer";
import { getDicePoolSuccessPercentage } from "./utils";
import { SelectLabelInputStepper } from "../Steppers";
import DifficultySlider from "./DifficultySlider/DifficultySlider";
import DifficultyList from "./DifficultyList/DifficultyList";

const useStyles = makeStyles(theme => ({
  root: {
    display: `flex`,
    justifyContent: `space-around`,
    alignItems: `center`,
    flexWrap: `wrap`,
    minHeight: `100%`,
    paddingTop: theme.spacing(3),
    "& section:not(:last-child)": {
      marginBottom: theme.spacing(6)
    },
    "& section:last-child": {
      paddingBottom: theme.spacing(9)
    }
  },
  section: {
    display: `flex`,
    justifyContent: `center`
  },
  successTextField: {
    margin: theme.spacing(1),
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1)
    },
    "& .MuiInputLabel-root": {
      transform: `translate(14px, 10px)`
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      transform: `translate(14px, -4px) scale(0.75)`
    }
  },
  buttonGroup: {
    margin: theme.spacing(1),
    height: `35px`
  },
  button: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    width: `95px`,
    height: `35px`
  },
  dropdownButton: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    height: `35px`
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: `20em`,
    maxWidth: `95vw`
  },
  margin: {
    marginLeft: theme.spacing(3.5),
    marginRight: theme.spacing(1)
  },
  slider: {
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(2.5)
  }
}));

const {
  updateSlider: _updateSlider,
  updateSliders: _updateSliders,
  updatePoolDieType: _updateDicePoolDieType,
  updatePoolDiceNumber: _updateDicePoolNumberOfDice,
  updatePoolOnesNegate: _updateDicePoolOnesNegateSuccess,
  updatePoolTargetNumber: _updateDicePoolTargetNumber,
  updatePoolTargetSuccesses: _updateDicePoolTargetSuccesses
} = actions.tools;
const selectToolsInfo = createSelector(state => state.tools, _.identity);

const minNumberOfDice = 0;
const maxNumberOfDice = 10;
const dicePoolOptions = [`d4`, `d6`, `d8`, `d10`, `d12`];
const DMTools = () => {
  const classes = useStyles();

  const {
    difficulties,
    sliders,
    dicePoolDieType,
    dicePoolNumberOfDice,
    dicePoolOnesNegateSuccess,
    dicePoolTargetNumber,
    dicePoolTargetSuccesses
  } = useSelector(selectToolsInfo);
  const dispatch = useDispatch();

  const [
    updateSlider,
    updateSliders,
    updateDicePoolDieType,
    updateDicePoolNumberOfDice,
    updateDicePoolOnesNegateSuccess,
    updateDicePoolTargetNumber,
    updateDicePoolTargetSuccesses
  ] = useMemo(
    () => [
      (...args) => dispatch(_updateSlider(...args)),
      (...args) => dispatch(_updateSliders(...args)),
      (...args) => dispatch(_updateDicePoolDieType(...args)),
      (...args) => dispatch(_updateDicePoolNumberOfDice(...args)),
      (...args) => dispatch(_updateDicePoolOnesNegateSuccess(...args)),
      (...args) => dispatch(_updateDicePoolTargetNumber(...args)),
      (...args) => dispatch(_updateDicePoolTargetSuccesses(...args))
    ],
    [dispatch]
  );

  const [open, setOpen] = useState(false);
  const [dicePoolOpen, setDicePoolOpen] = useState(false);
  const anchorRef = useRef(null);
  const dicePoolAnchorRef = useRef(null);

  // 0 - Easy is [70%-100%] success rate
  // 1 - Average is [40%-70%) success rate
  // 2 - Hard is [1% to 40%) success rate
  // 3 - Angry GM is [0% to 1%) success rate
  const bounds = [1.0001, 0.7, 0.4, 0.01, 0];
  const boundsLabels = [
    { primary: `Easy`, secondary: `Piece of cake` },
    { primary: `Average`, secondary: `Hooray for normal` },
    { primary: `Hard`, secondary: `Gotta earn this one` },
    { primary: `Angry GM`, secondary: `Not always available` }
  ];

  const resetDCsForDifficulty = difficulty => {
    const activeSliders = _.reject(sliders, `disabled`);
    if (0 === activeSliders.length) return;

    const upperBound = bounds[difficulty];
    const lowerBound = bounds[difficulty + 1];

    const targetProbability = _.random(lowerBound, upperBound);
    const variance = 2; // number of steps either way

    let rawDCs = _.chain(Array(activeSliders.length))
      .fill(Math.pow(targetProbability, 1 / activeSliders.length))
      // get initial spread
      .map(probability => {
        let DC =
          _.toInteger(21 - probability * 20) + _.random(-variance, variance);
        DC = Math.min(DC, 20);
        DC = Math.max(DC, 1);
        return DC;
      })
      // make sure the calculated probability is w/in the selected range
      .thru(DCs => {
        let rawSuccessPercentage = DCs.reduce(
          (acc, DC) => (acc * (21 - DC)) / 20,
          1
        );

        while (rawSuccessPercentage < lowerBound) {
          console.log(`adjusting up ${rawSuccessPercentage}`);
          const i = _.random(DCs.length - 1);
          DCs[i] = Math.max(DCs[i] - 1, 1);

          rawSuccessPercentage = DCs.reduce(
            (acc, DC) => (acc * (21 - DC)) / 20,
            1
          );
        }

        while (rawSuccessPercentage >= upperBound) {
          console.log(`adjusting down ${rawSuccessPercentage}`);
          const i = _.random(DCs.length - 1);
          DCs[i] = Math.min(DCs[i] + 1, 20);

          rawSuccessPercentage = DCs.reduce(
            (acc, DC) => (acc * (21 - DC)) / 20,
            1
          );
        }

        return DCs;
      })
      // Prevent exact same settings from being chosen
      .thru(DCs => {
        const currentActiveDCs = _.map(
          activeSliders,
          slider => slider.DC - slider.mod
        );

        if (!_.isEqual(DCs, currentActiveDCs)) return DCs;

        let rawSuccessPercentage = DCs.reduce(
          (acc, DC) => (acc * (21 - DC)) / 20,
          1
        );

        while (
          _.isEqual(DCs, currentActiveDCs) ||
          rawSuccessPercentage <= lowerBound ||
          rawSuccessPercentage > upperBound
        ) {
          const indexToNudge = _.random(DCs.length - 1);

          switch (DCs[indexToNudge]) {
            case 1:
              DCs[indexToNudge] = 2;
              break;
            case 20:
              DCs[indexToNudge] = 19;
              break;
            default:
              Math.abs(rawSuccessPercentage - upperBound) <
              Math.abs(rawSuccessPercentage - lowerBound)
                ? (DCs[indexToNudge] = DCs[indexToNudge] + 1)
                : (DCs[indexToNudge] = DCs[indexToNudge] - 1);
              break;
          }

          rawSuccessPercentage = DCs.reduce(
            (acc, DC) => (acc * (21 - DC)) / 20,
            1
          );
        }

        return DCs;
      })
      .value();

    const newSliders = sliders.map(({ disabled, DC, mod }) => ({
      disabled,
      DC: disabled ? DC : rawDCs.shift() + mod,
      mod
    }));

    updateSliders({ sliders: newSliders });
  };

  const handleMenuItemClick = (event, difficulty) => {
    resetDCsForDifficulty(difficulty);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const resetDicePoolForDifficulty = difficulty => {
    const upperBound = bounds[difficulty];
    const lowerBound = bounds[difficulty + 1];
    const numberOfSides = _.toInteger(dicePoolDieType.substr(1));

    let validSettings = [];
    // find all available options for current settings
    // there should be at least one
    for (let i = 1; i <= numberOfSides; i++) {
      for (let j = 1; j <= dicePoolNumberOfDice; j++) {
        // Don't include current settings. This  can reduce the number of
        // valid options to 0
        if (i === dicePoolTargetNumber && j === dicePoolTargetSuccesses)
          continue;

        const probability = getDicePoolSuccessPercentage(
          numberOfSides,
          dicePoolNumberOfDice,
          i,
          j,
          dicePoolOnesNegateSuccess
        );

        if (probability >= lowerBound && probability < upperBound)
          validSettings.push({ targetNumber: i, targetSuccesses: j });
      }
    }

    // Only update if there is more than one option for this difficulty
    const target = _.sample(validSettings);
    if (target) {
      const { targetNumber, targetSuccesses } = target;
      updateDicePoolTargetNumber({ targetNumber });
      updateDicePoolTargetSuccesses({ targetSuccesses });
    }
  };

  const handleDicePoolMenuItemClick = (event, difficulty) => {
    resetDicePoolForDifficulty(difficulty);
    setDicePoolOpen(false);
  };

  const handleDicePoolToggle = () => {
    setDicePoolOpen(!dicePoolOpen);
  };

  const handleDicePoolClose = event => {
    if (
      dicePoolAnchorRef.current &&
      dicePoolAnchorRef.current.contains(event.target)
    ) {
      return;
    }

    setDicePoolOpen(false);
  };

  const sliderSuccessPercentage = sliders.reduce(
    (acc, slider) =>
      slider.disabled ? acc : (acc * (21 + slider.mod - slider.DC)) / 20,
    1
  );

  let currentDifficulty = 3;
  for (let i = bounds.length - 2; i >= 0; i--) {
    if (sliderSuccessPercentage >= bounds[i]) currentDifficulty--;
  }

  const numberOfSides = _.toInteger(dicePoolDieType.substr(1));
  const dicePoolSuccessPercentage = getDicePoolSuccessPercentage(
    numberOfSides,
    dicePoolNumberOfDice,
    dicePoolTargetNumber,
    dicePoolTargetSuccesses,
    dicePoolOnesNegateSuccess
  );

  let dicePoolDifficulty = 3;
  for (let i = bounds.length - 2; i >= 0; i--) {
    if (dicePoolSuccessPercentage >= bounds[i] && dicePoolDifficulty > 0)
      dicePoolDifficulty--;
  }

  return (
    <div className={classes.root}>
      <section>
        <div className={classes.section}>
          <Typography variant="h6">d20 Rollover Probability</Typography>
        </div>
        <div className={classes.section}>
          {sliders.map((slider, index) => (
            <DifficultySlider
              key={`difficultySlider${index}`}
              {...slider}
              onToggle={evt => {
                const newSlider = {
                  ...slider,
                  disabled: !evt.target.checked
                };
                updateSlider({ slider: newSlider, index });
              }}
              onDCChange={(evt, value) => {
                const newSlider = {
                  ...slider,
                  DC: value
                };
                updateSlider({ slider: newSlider, index });
              }}
              onModChange={mod => {
                console.log({
                  ...slider,
                  DC: slider.DC + mod - slider.mod,
                  mod
                });
                const newSlider = {
                  ...slider,
                  DC: slider.DC + mod - slider.mod,
                  mod
                };
                updateSlider({ slider: newSlider, index });
              }}
            />
          ))}
        </div>
        <div className={classes.section}>
          <Typography variant="body1" color="secondary">
            Note: Modifiers Range from &ndash;50 to 50
          </Typography>
        </div>
        <div className={classes.section}>
          <ButtonGroup
            variant="contained"
            color="primary"
            aria-label="Rollover Difficulty Selector"
            ref={anchorRef}
            className={classes.buttonGroup}
          >
            <Button
              className={classes.button}
              onClick={() => {
                resetDCsForDifficulty(currentDifficulty);
              }}
            >
              {difficulties[currentDifficulty]}
            </Button>
            <Button
              className={classes.dropDownButton}
              color="primary"
              size="small"
              aria-controls={open ? `split-button-menu` : undefined}
              aria-expanded={open ? `true` : undefined}
              aria-label="Select Difficulty"
              aria-haspopup="menu"
              onClick={handleToggle}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 2 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    `bottom` === placement ? `center top` : `center bottom`
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="difficulty-menu">
                      {_.map(difficulties, (option, index) => {
                        const disabled = (
                          _.reject(sliders, `disabled`).length
                            ? Result.Ok(false)
                            : Result.Error(true)
                        )
                          .map(notUsed => {
                            if (3 === index) {
                              const activeSliders = _.reject(
                                sliders,
                                `disabled`
                              ).length;

                              return (
                                Math.pow(1 / 20, activeSliders) > bounds[3]
                              );
                            }

                            return false;
                          })
                          .merge();

                        return (
                          <MenuItem
                            key={option}
                            selected={index === currentDifficulty}
                            onClick={evt => {
                              handleMenuItemClick(evt, index);
                            }}
                            disabled={disabled}
                          >
                            {option}
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
          <TextField
            variant="outlined"
            label="Success Chance"
            value={_.round(sliderSuccessPercentage * 100, 5)}
            className={classes.successTextField}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
          />
        </div>
      </section>
      <section>
        <DifficultyList bounds={bounds} labels={boundsLabels} />
      </section>
      <section>
        <div className={classes.section}>
          <Typography variant="h6">Dice Pool Probability</Typography>
        </div>
        <div className={classes.section}>
          <SelectLabelInputStepper
            id="dice-pool-select"
            selectLabel="Die Type"
            selectValue={dicePoolDieType}
            selectOptions={dicePoolOptions}
            value={dicePoolNumberOfDice}
            min={minNumberOfDice}
            max={maxNumberOfDice}
            onSelectChange={newDieType => {
              updateDicePoolDieType({ dieType: newDieType });
              updateDicePoolTargetNumber({
                targetNumber: Math.min(
                  dicePoolTargetNumber,
                  _.toInteger(newDieType.substr(1))
                )
              });
            }}
            onChange={numberOfDice => {
              updateDicePoolNumberOfDice({ numberOfDice });
              updateDicePoolTargetSuccesses({
                targetSuccesses: Math.min(numberOfDice, dicePoolTargetSuccesses)
              });
            }}
          />
        </div>
        <div className={classes.section}>
          <Slider
            className={classes.slider}
            defaultValue={1}
            valueLabelDisplay="auto"
            marks
            value={dicePoolTargetNumber}
            getAriaValueText={() => `Target Roll: ${dicePoolTargetNumber} `}
            aria-labelledby="dice-pool-success-range"
            step={1}
            min={1}
            max={_.toInteger(dicePoolDieType.substr(1))}
            onChange={(evt, targetNumber) => {
              updateDicePoolTargetNumber({ targetNumber });
            }}
          />
        </div>
        <Typography
          className={classes.margin}
          id="dice-pool-success-range"
          gutterBottom
        >
          Dice Pool Target Roll: {dicePoolTargetNumber}
        </Typography>
        <div className={classes.section}>
          <Slider
            className={classes.slider}
            defaultValue={1}
            valueLabelDisplay="auto"
            marks
            value={dicePoolTargetSuccesses}
            getAriaValueText={() =>
              `Number of Successes: ${dicePoolTargetSuccesses}`
            }
            aria-labelledby="dice-pool-number-successes"
            step={1}
            min={0}
            max={dicePoolNumberOfDice}
            onChange={(evt, targetNumber) => {
              console.log(targetNumber);
              updateDicePoolTargetSuccesses({ targetSuccesses: targetNumber });
            }}
          />
        </div>
        <Typography
          className={classes.margin}
          id="dice-pool-number-successes"
          gutterBottom
        >
          Dice Pool Number of Successes: {dicePoolTargetSuccesses}
        </Typography>
        <FormControlLabel
          className={classes.margin}
          control={
            <Switch
              color="primary"
              checked={dicePoolOnesNegateSuccess}
              onChange={evt => {
                updateDicePoolOnesNegateSuccess({ negate: evt.target.checked });
              }}
              value="Ones Negate Success"
            />
          }
          label="1s negate success"
        />
        <div className={classes.section}>
          <ButtonGroup
            variant="contained"
            color="primary"
            aria-label="Dice Pool Difficulty Selector"
            ref={dicePoolAnchorRef}
            className={classes.buttonGroup}
          >
            <Button
              className={classes.button}
              onClick={() => {
                resetDicePoolForDifficulty(dicePoolDifficulty);
              }}
            >
              {difficulties[dicePoolDifficulty] || dicePoolDifficulty}
            </Button>
            <Button
              className={classes.dropDownButton}
              color="primary"
              size="small"
              aria-controls={open ? `split-button-menu` : undefined}
              aria-expanded={open ? `true` : undefined}
              aria-label="Select Difficulty"
              aria-haspopup="menu"
              onClick={handleDicePoolToggle}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={dicePoolOpen}
            anchorEl={dicePoolAnchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 2 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    `bottom` === placement ? `center top` : `center bottom`
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleDicePoolClose}>
                    <MenuList id="dice-pool-difficulty-menu">
                      {_.map(difficulties, (option, index) => {
                        let disabled = false;

                        // Check if Angry GM difficulty is achievable w/ the current settings
                        if (3 === index) {
                          disabled =
                            getDicePoolSuccessPercentage(
                              _.toInteger(dicePoolDieType.substr(1)),
                              dicePoolNumberOfDice,
                              _.toInteger(dicePoolDieType.substr(1)),
                              dicePoolNumberOfDice,
                              dicePoolOnesNegateSuccess
                            ) > bounds[3];
                        }

                        return (
                          <MenuItem
                            key={option}
                            selected={index === dicePoolDifficulty}
                            onClick={evt => {
                              handleDicePoolMenuItemClick(evt, index);
                            }}
                            disabled={disabled}
                          >
                            {option}
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
          <TextField
            variant="outlined"
            label="Success Chance"
            value={_.round(dicePoolSuccessPercentage * 100, 5)}
            className={classes.successTextField}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
          />
        </div>
      </section>
    </div>
  );
};

export default DMTools;
